import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Artikel as ArtikelTyp, ArtikelImportErgebnis } from '../types';
import artikelService from '../services/artikelService';
import LeistungszeitpunktAuswahl from '../components/LeistungszeitpunktAuswahl';
import { useAuth } from '../contexts/AuthContext';
import {
  Button, Card, PageHeader, RowAction, LoadingScreen, EmptyState, Icon,
  Modal, Field, Input, FormActions, inputClass, tableCard, th, td, tableRow,
  FilterStand, filterAktiv,
} from '../components/ui';
import { useStueckweise } from '../utils/stueckweise';
import { ankunft, markiereNeu } from '../utils/ankunft';
import { formatPreis, formatMwst, zahlZuText, textZuZahl, STANDARD_MWST } from '../utils/preis';

/**
 * Anlegen/Bearbeiten eines Artikels. Beim Bearbeiten ist die Artikelnummer gesperrt – sie ist
 * der Schlüssel, über den der Excel-Import bestehende Artikel wiederfindet.
 */
const ArtikelFormModal: React.FC<{
  artikel?: ArtikelTyp | null;
  alleArtikel: ArtikelTyp[];
  istAdmin: boolean;
  onClose: () => void;
  onSaved: (artikelId?: number) => void;
}> = ({ artikel, alleArtikel, istAdmin, onClose, onSaved }) => {
  const editing = artikel ?? null;
  const [periodeId, setPeriodeId] = useState<string>(editing?.periodeId ? String(editing.periodeId) : '');
  // Ersetzen in allen Verträgen (ADMIN): Anzahl betroffener Positionen und gewählter Ersatz.
  const [verwendung, setVerwendung] = useState<number | null>(null);
  const [ersatzSuche, setErsatzSuche] = useState('');
  const [ersatz, setErsatz] = useState<ArtikelTyp | null>(null);

  useEffect(() => {
    if (editing && istAdmin) artikelService.verwendung(editing.id).then(setVerwendung).catch(() => setVerwendung(null));
  }, [editing, istAdmin]);

  const ersatzTreffer = useMemo(() => {
    const q = ersatzSuche.trim().toLowerCase();
    if (!q || !editing) return [];
    return alleArtikel
      .filter(a => a.id !== editing.id && `${a.artikelnummer} ${a.bezeichnung}`.toLowerCase().includes(q))
      .slice(0, 10);
  }, [alleArtikel, ersatzSuche, editing]);

  const ersetzen = async () => {
    if (!editing || !ersatz) return;
    if (!window.confirm(
      `Artikel ${editing.artikelnummer} in ${verwendung ?? 'allen'} Positionen laufender Verträge durch ` +
      `${ersatz.artikelnummer} „${ersatz.bezeichnung}“ ersetzen? Bereits gestellte Rechnungen bleiben unverändert.`,
    )) return;
    setSaving(true);
    try {
      const anzahl = await artikelService.ersetzen(editing.id, ersatz.id);
      toast.success(`Artikel in ${anzahl} Position${anzahl === 1 ? '' : 'en'} ersetzt`);
      onSaved();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Artikel konnte nicht ersetzt werden');
    } finally {
      setSaving(false);
    }
  };
  const [artikelnummer, setArtikelnummer] = useState(editing?.artikelnummer ?? '');
  const [bezeichnung, setBezeichnung] = useState(editing?.bezeichnung ?? '');
  const [preisText, setPreisText] = useState(zahlZuText(editing?.preis));
  const [mwstText, setMwstText] = useState(zahlZuText(editing?.mwstSatz ?? STANDARD_MWST));
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const preis = textZuZahl(preisText);
    if (preis === undefined) { toast.error('Preis ist keine gültige Zahl'); return; }
    const mwstSatz = textZuZahl(mwstText);
    if (mwstSatz === undefined || mwstSatz === null || mwstSatz > 100) {
      toast.error('MwSt-Satz muss zwischen 0 und 100 liegen'); return;
    }

    setSaving(true);
    try {
      if (editing) {
        await artikelService.update(editing.id, {
          bezeichnung: bezeichnung.trim(), preis, mwstSatz,
          periodeId: periodeId ? parseInt(periodeId, 10) : null, active: editing.active,
        });
        markiereNeu(`artikel-${editing.id}`);
        toast.success('Artikel gespeichert');
        onSaved(editing.id);
      } else {
        const neu = await artikelService.create({
          artikelnummer: artikelnummer.trim(), bezeichnung: bezeichnung.trim(), preis, mwstSatz,
          periodeId: periodeId ? parseInt(periodeId, 10) : null,
        });
        markiereNeu(`artikel-${neu.id}`);
        toast.success('Artikel angelegt');
        onSaved(neu.id);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Artikel konnte nicht gespeichert werden');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editing || !window.confirm(`Artikel „${editing.artikelnummer} ${editing.bezeichnung}“ löschen? Laufende Verträge behalten ihre Positionen.`)) return;
    setSaving(true);
    try {
      await artikelService.delete(editing.id);
      toast.success('Artikel gelöscht');
      onSaved();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Artikel konnte nicht gelöscht werden');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={editing ? `Artikel ${editing.artikelnummer}` : 'Neuer Artikel'} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field
          label="Artikelnummer"
          required={!editing}
          hint={editing ? 'Nicht änderbar – darüber ordnet der Excel-Import den Artikel zu.' : undefined}
        >
          <Input
            required
            disabled={!!editing}
            maxLength={50}
            value={artikelnummer}
            onChange={e => setArtikelnummer(e.target.value)}
          />
        </Field>
        <Field label="Bezeichnung" required>
          <Input required maxLength={255} value={bezeichnung} onChange={e => setBezeichnung(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Preis (netto, €)" hint="Leer lassen, wenn der Artikel ohne Preis geführt wird.">
            <Input
              inputMode="decimal"
              placeholder="z. B. 12,50"
              value={preisText}
              onChange={e => setPreisText(e.target.value)}
            />
          </Field>
          <Field label="MwSt (%)" required hint="Bleibt beim Excel-Import erhalten.">
            <Input
              required
              inputMode="decimal"
              placeholder="19"
              value={mwstText}
              onChange={e => setMwstText(e.target.value)}
            />
          </Field>
        </div>

        <Field
          label="Leistungszeitpunkt"
          hint="Wann im Jahr die Leistung erbracht wird – steht dann mit Datum auf der Rechnung. Bleibt beim Excel-Import erhalten."
        >
          <LeistungszeitpunktAuswahl value={periodeId} onChange={setPeriodeId} />
        </Field>

        {/* Ändert die Warenwirtschaft eine Artikelnummer, lassen sich alle laufenden Verträge umstellen. */}
        {editing && istAdmin && verwendung != null && verwendung > 0 && (
          <div className="rounded-lg border border-gray-200 p-3">
            <p className="text-sm font-medium text-gray-700">In allen Verträgen ersetzen</p>
            <p className="mb-2 text-xs text-gray-500">
              Wird in {verwendung} Position{verwendung === 1 ? '' : 'en'} laufender Verträge verwendet.
            </p>
            {ersatz ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-sm text-gray-900">→ {ersatz.artikelnummer} {ersatz.bezeichnung}</span>
                <Button type="button" size="sm" variant="secondary" onClick={() => setErsatz(null)}>Ändern</Button>
                <Button type="button" size="sm" loading={saving} onClick={ersetzen}>Ersetzen</Button>
              </div>
            ) : (
              <>
                <input
                  placeholder="Neuen Artikel suchen…"
                  value={ersatzSuche}
                  onChange={e => setErsatzSuche(e.target.value)}
                  className={inputClass}
                />
                {ersatzTreffer.length > 0 && (
                  <ul className="mt-2 max-h-40 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200">
                    {ersatzTreffer.map(a => (
                      <li key={a.id}>
                        <button type="button" onClick={() => { setErsatz(a); setErsatzSuche(''); }} className="flex w-full gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50">
                          <span className="text-xs tabular-nums text-gray-400">{a.artikelnummer}</span>
                          <span className="min-w-0 flex-1 truncate">{a.bezeichnung}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        )}

        <FormActions>
          {editing && (
            <Button type="button" variant="danger" className="mr-auto" disabled={saving} onClick={handleDelete}>
              Löschen
            </Button>
          )}
          <Button type="button" variant="secondary" onClick={onClose}>Abbrechen</Button>
          <Button type="submit" loading={saving}>{editing ? 'Speichern' : 'Anlegen'}</Button>
        </FormActions>
      </form>
    </Modal>
  );
};

/**
 * Artikel-Preisliste. Wird per Excel-Export aus der Warenwirtschaft importiert (nur ADMIN) und
 * kann von jedem Mitarbeiter gepflegt werden (anlegen/bearbeiten/löschen). Preise sind netto.
 */
const Artikel: React.FC = () => {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState<ArtikelTyp[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [importing, setImporting] = useState(false);
  const [ergebnis, setErgebnis] = useState<ArtikelImportErgebnis | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<ArtikelTyp | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Nach dem Speichern zur Seite blättern, auf der der Artikel steht, damit er sichtbar aufleuchtet.
  const springeZu = useRef<number | null>(null);

  const load = useCallback(async () => {
    try { setItems(await artikelService.getAll()); }
    catch { toast.error('Artikel konnten nicht geladen werden'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(a => `${a.artikelnummer} ${a.bezeichnung}`.toLowerCase().includes(q));
  }, [items, query]);

  // Kein Blättern: die Liste läuft durch und lädt beim Scrollen still nach. Ein gerade gespeicherter
  // Artikel wird mitgerendert, damit er aufleuchten und ins Bild scrollen kann.
  const { sichtbar, Ende, zeigeBis } = useStueckweise(filtered);
  useEffect(() => {
    if (springeZu.current == null) return;
    const index = filtered.findIndex(a => a.id === springeZu.current);
    springeZu.current = null;
    if (index >= 0) zeigeBis(index);
  }, [filtered, zeigeBis]);

  const importFile = async (file: File) => {
    setImporting(true);
    setErgebnis(null);
    try {
      const result = await artikelService.importExcel(file);
      setErgebnis(result);
      toast.success(`Preisliste importiert: ${result.angelegt} angelegt, ${result.aktualisiert} aktualisiert`);
      await load();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Preisliste konnte nicht importiert werden');
    } finally {
      setImporting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) importFile(file);
    e.target.value = '';
  };

  const openNew = () => { setEditing(null); setShowForm(true); };
  const openEdit = (artikel: ArtikelTyp) => { setEditing(artikel); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditing(null); };
  const handleSaved = (artikelId?: number) => { springeZu.current = artikelId ?? null; closeForm(); load(); };

  if (loading) return <LoadingScreen />;

  return (
    <div className="mx-auto max-w-6xl p-6">
      <PageHeader
        title="Artikel"
        subtitle={`${items.length} Artikel · Preise netto`}
        actions={
          <>
            {isAdmin() && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={handleInputChange}
                  className="hidden"
                />
                <Button
                  variant="secondary"
                  onClick={() => fileInputRef.current?.click()}
                  loading={importing}
                  icon={<Icon.Upload className="h-4 w-4" />}
                >
                  {importing ? 'Importiere…' : 'Preisliste importieren'}
                </Button>
              </>
            )}
            <Button onClick={openNew} icon={<Icon.Plus className="h-4 w-4" />}>Artikel anlegen</Button>
          </>
        }
      />

      {showForm && (
        <ArtikelFormModal artikel={editing} alleArtikel={items} istAdmin={isAdmin()} onClose={closeForm} onSaved={handleSaved} />
      )}

      {/* Ergebnis des letzten Imports (nur nach einem Upload sichtbar) */}
      {ergebnis && (
        <Card className="mb-4 border-brand-200 bg-brand-50/50 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-semibold text-gray-900">Import abgeschlossen</p>
              <p className="mt-1 text-sm text-gray-700">
                {ergebnis.angelegt} angelegt · {ergebnis.aktualisiert} aktualisiert ·{' '}
                {ergebnis.unveraendert} unverändert
                {ergebnis.uebersprungen > 0 && ` · ${ergebnis.uebersprungen} übersprungen`}
              </p>
              {ergebnis.warnungen.length > 0 && (
                <ul className="mt-2 list-disc space-y-0.5 pl-5 text-xs text-amber-700">
                  {ergebnis.warnungen.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              )}
            </div>
            <RowAction onClick={() => setErgebnis(null)}>Schließen</RowAction>
          </div>
        </Card>
      )}

      {/* Suche */}
      <Card className="mb-4 flex flex-wrap items-center gap-3 p-3">
        <div className="relative min-w-[200px] flex-1">
          <span className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 ${query.trim() ? 'text-brand-600' : 'text-gray-400'}`}><Icon.Search className="h-4 w-4" /></span>
          <input
            placeholder="Suche nach Artikelnummer oder Bezeichnung…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            className={`${inputClass} pl-9 ${query.trim() ? filterAktiv : ''}`}
          />
        </div>
        <FilterStand treffer={filtered.length} gesamt={items.length} einheit="Artikel" aktiv={query.trim() !== ''} onZuruecksetzen={() => setQuery('')} />
      </Card>

      <div className={tableCard}>
        {filtered.length === 0 ? (
          <EmptyState
            icon={<Icon.Doc />}
            title={items.length === 0 ? 'Noch keine Artikel' : 'Keine Treffer für diese Suche'}
            hint={items.length === 0
              ? (isAdmin()
                ? 'Die Preisliste kommt als Excel-Export aus der Warenwirtschaft; einzelne Artikel lassen sich auch von Hand anlegen.'
                : 'Die Preisliste importiert ein Administrator; einzelne Artikel können Sie selbst anlegen.')
              : 'Suchen Sie nach Artikelnummer oder einem Teil der Bezeichnung.'}
            action={items.length === 0 ? (
              <>
                {isAdmin() && (
                  <Button
                    variant="secondary"
                    onClick={() => fileInputRef.current?.click()}
                    loading={importing}
                    icon={<Icon.Upload className="h-4 w-4" />}
                  >
                    Preisliste importieren
                  </Button>
                )}
                <Button onClick={openNew} icon={<Icon.Plus className="h-4 w-4" />}>Artikel anlegen</Button>
              </>
            ) : (
              <Button variant="secondary" onClick={() => setQuery('')}>Suche zurücksetzen</Button>
            )}
          />
        ) : (
          <>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50/80">
              <tr>
                <th className={`${th} !px-5 w-24`}>Nr.</th>
                <th className={`${th} !px-5`}>Bezeichnung</th>
                {/* MwSt steht als Unterzeile im Preis-Feld – eine vierte Spalte sprengt die
                    iPad-Hochkant-Breite (~504 px). */}
                <th className={`${th} !px-5 text-right`}>Preis netto</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sichtbar.map(a => { const neu = ankunft(`artikel-${a.id}`); return (
                <tr
                  key={a.id}
                  ref={neu.ref}
                  className={`${tableRow} cursor-pointer ${neu.className}`}
                  onClick={() => openEdit(a)}
                >
                  {/* Nummer (Schlüssel zur Warenwirtschaft) als eigene schmale Spalte, die Bezeichnung
                      bleibt die Hauptsache; darunter der Leistungszeitpunkt, wenn es einen gibt. */}
                  <td className={`${td} !px-5 whitespace-nowrap tabular-nums text-gray-500`}>{a.artikelnummer}</td>
                  <td className={`${td} !px-5 break-words`}>
                    <span className="block font-medium text-gray-900">{a.bezeichnung}</span>
                    {a.periodeName && (
                      <span className="block text-xs text-gray-500">
                        Leistung {a.periodeName}{a.periodeTerminText ? ` (${a.periodeTerminText})` : ''}
                      </span>
                    )}
                  </td>
                  <td className={`${td} !px-5 whitespace-nowrap text-right tabular-nums ${a.preis == null ? 'text-gray-400' : 'font-medium text-gray-900'}`}>
                    {/* Sammel-/Kopfartikel der Preisliste haben keinen Preis – dann auch keine MwSt-Zeile. */}
                    {a.preis == null ? <span className="text-xs">ohne Preis</span> : <>
                      {formatPreis(a.preis)}
                      <span className="block text-xs font-normal text-gray-400">{formatMwst(a.mwstSatz)} MwSt</span>
                    </>}
                  </td>
                </tr>
              ); })}
            </tbody>
          </table>
          {Ende}
          </>
        )}
      </div>
    </div>
  );
};

export default Artikel;
